<template>
    <section>
        <div class="content mt-6 mb-6" v-if="!ready">
            <h5 class="has-text-grey"><i class="fal fa-tennis-ball fa-spin mr-3"></i>Loading. Please wait...</h5>
        </div>

        <div class="content has-text-centered" v-else-if="!enabled">
            <h3 class="pt-6 pb-6 has-text-grey"><i class="fal fa-message-captions mr-2"></i> This section is currently not available for this trip.</h3>
        </div>

        <div class="content" v-else>
            <h3 class="mt-2 mb-5 ml-2 is-hidden-desktop"><i class="fal fa-hashtag fa-fw mr-3"></i>Experience</h3>

            <push-opt-in class="mb-4"/>

            <!-- Mobile: announcements section above the feed -->
            <div class="is-hidden-desktop" v-if="twoColumn && (railAnnouncements.length || isStaff)">
                <div class="columns is-gapless is-mobile mb-3">
                    <div class="column is-narrow">
                        <i class="fal fa-bullhorn fa-fw is-size-4 mr-3"></i>
                    </div>
                    <div class="column">
                        <h4>Announcements</h4>
                    </div>
                    <div class="column is-narrow ml-5" v-if="isStaff">
                        <b-button type="is-primary" size="is-small" @click="announcementComposer = true"><i class="fas fa-plus mr-3"></i>Announcement</b-button>
                    </div>
                </div>
                <announcements-rail
                    :posts="railAnnouncements"
                    :reg-id="registration.id"
                    :is-staff="isStaff"
                    @changed="loadRail"
                />
                <p class="has-text-grey is-italic" v-if="!railAnnouncements.length">No recent announcements.</p>
                <hr class="mt-5 mb-5" style="border-bottom: 1px solid #ddd"/>
            </div>

            <div class="columns">
                <div class="column feed-column" :class="{'is-two-thirds': twoColumn}">
                    <div class="columns is-gapless is-mobile mb-3">
                        <div class="column is-narrow">
                            <i class="fal fa-stream fa-fw is-size-4 mr-3"></i>
                        </div>
                        <div class="column">
                            <h4>Activity</h4>
                        </div>
                        <div class="column is-narrow ml-5" v-if="wallOpen || isStaff">
                            <b-button type="is-primary" size="is-small" @click="wallComposer = true"><i class="fas fa-plus mr-3"></i>New Post</b-button>
                        </div>
                    </div>

                    <div class="content mt-6 mb-6" v-if="loadingFeed">
                        <h5 class="has-text-grey"><i class="fal fa-tennis-ball fa-spin mr-3"></i>Loading...</h5>
                    </div>

                    <div v-else>
                        <p class="has-text-grey is-italic mt-5" v-if="!visibleFeed.length">
                            {{ railAnnouncements.length ? 'Nothing else has been posted yet.' : 'Nothing has been posted yet.' }}
                        </p>

                        <post-card
                            v-for="post in visibleFeed"
                            :key="post.id"
                            :post="post"
                            :reg-id="registration.id"
                            :is-staff="isStaff"
                            @changed="refetchPost(post.id)"
                        />

                        <div class="has-text-centered mt-5" v-if="!feedDone && feedPosts.length">
                            <b-button size="is-small" @click="loadMore" :loading="loadingMore">Load earlier activity</b-button>
                        </div>
                    </div>
                </div>

                <!-- Desktop right rail: prominent announcements + media grid -->
                <div class="column is-one-third is-hidden-touch rail-column" v-if="twoColumn">
                    <div class="columns is-gapless is-mobile mb-3">
                        <div class="column is-narrow">
                            <i class="fal fa-bullhorn fa-fw is-size-4 mr-3"></i>
                        </div>
                        <div class="column">
                            <h4>Announcements</h4>
                        </div>
                        <div class="column is-narrow ml-5" v-if="isStaff">
                            <b-button type="is-primary" size="is-small" @click="announcementComposer = true"><i class="fas fa-plus mr-3"></i>Announcement</b-button>
                        </div>
                    </div>

                    <announcements-rail
                        :posts="railAnnouncements"
                        :reg-id="registration.id"
                        :is-staff="isStaff"
                        @changed="loadRail"
                    />
                    <p class="has-text-grey is-italic" v-if="!railAnnouncements.length">No recent announcements.</p>

                    <media-grid
                        class="mt-6"
                        :media="media"
                        :reg-id="registration.id"
                    />
                </div>
            </div>
        </div>

        <composer
            mode="announcement"
            :open="announcementComposer"
            :reg-id="registration.id"
            @close="announcementComposer = false"
            @saved="onSaved('announcement')"
        />
        <composer
            mode="wall"
            :open="wallComposer"
            :reg-id="registration.id"
            @close="wallComposer = false"
            @saved="onSaved('wall')"
        />
    </section>
</template>

<script>
    import {mapGetters} from 'vuex';
    import {client as http} from '@/http_client';
    import * as socket from '@/socket';
    import PushOptIn from '@/components/PushOptIn.vue';
    import PostCard from './PostCard.vue';
    import Composer from './Composer.vue';
    import AnnouncementsRail from './AnnouncementsRail.vue';
    import MediaGrid from './MediaGrid.vue';

    // An announcement this recent is shown prominently in the rail and
    // therefore left OUT of the activity feed (it would be redundant there).
    const RAIL_WINDOW_HOURS = 6;
    const RAIL_MAX = 3;

    export default {
        components: {PushOptIn, PostCard, Composer, AnnouncementsRail, MediaGrid},
        data: function() {
            return {
                feedPosts: [],
                loadingFeed: true,
                loadingMore: false,
                feedDone: false,
                railAnnouncements: [],
                media: [],
                announcementComposer: false,
                wallComposer: false
            };
        },
        computed: {
            ...mapGetters(['trip', 'registration']),
            ready: function() {
                return !!(this.trip.id && this.registration.id);
            },
            enabled: function() {
                return this.trip.app_experience === 1;
            },
            isStaff: function() {
                return this.registration.type === 'staff' || this.registration.second_type === 'staff';
            },
            wallOpen: function() {
                return this.trip.app_wall_staff_only !== 1;
            },
            canPost: function() {
                return this.isStaff || this.wallOpen;
            },
            // Wall open to participants → the full two-column experience.
            // Wall locked → announcements-focused single column (staff still
            // get the full layout so they see what they're publishing).
            twoColumn: function() {
                return this.wallOpen || this.isStaff;
            },
            // Whatever the rail displays is excluded from the feed.
            visibleFeed: function() {
                if (!this.twoColumn || !this.railAnnouncements.length) return this.feedPosts;
                const railIds = this.railAnnouncements.map(p => p.id);
                return this.feedPosts.filter(p => railIds.indexOf(p.id) === -1);
            }
        },
        methods: {
            loadAll: function() {
                this.loadFeed();
                this.loadRail();
                this.loadMedia();
            },
            loadFeed: function() {
                if (!this.registration.id) return;
                http.get(`/app/experience/${this.registration.id}/feed`, {force: true}).then(response => {
                    this.feedPosts = response.data.posts;
                    this.feedDone = response.data.posts.length < response.data.limit;
                    this.loadingFeed = false;
                });
            },
            loadMore: function() {
                const oldest = this.feedPosts.length ? this.feedPosts[this.feedPosts.length - 1].id : null;
                if (!oldest) return;
                this.loadingMore = true;
                http.get(`/app/experience/${this.registration.id}/feed?before=${oldest}`, {force: true}).then(response => {
                    this.feedPosts = this.feedPosts.concat(response.data.posts);
                    this.feedDone = response.data.posts.length < response.data.limit;
                    this.loadingMore = false;
                });
            },
            loadRail: function() {
                if (!this.registration.id || !this.twoColumn) return;
                http.get(`/app/experience/${this.registration.id}/announcements?limit=10`, {force: true}).then(response => {
                    const cutoff = Date.now() - RAIL_WINDOW_HOURS * 3600 * 1000;
                    this.railAnnouncements = response.data.posts
                        .filter(p => new Date(p.created_at).getTime() >= cutoff)
                        .slice(0, RAIL_MAX);
                });
            },
            loadMedia: function() {
                if (!this.registration.id || !this.twoColumn) return;
                http.get(`/app/experience/${this.registration.id}/media?limit=12`, {force: true}).then(response => {
                    this.media = response.data.media;
                });
            },
            refetchPost: function(postId) {
                http.get(`/app/experience/${this.registration.id}/posts/${postId}`, {force: true}).then(response => {
                    const i = this.feedPosts.findIndex(p => p.id === postId);
                    if (i !== -1) this.$set(this.feedPosts, i, response.data);
                }).catch(err => {
                    if (err.response && err.response.status === 404) {
                        this.feedPosts = this.feedPosts.filter(p => p.id !== postId);
                    }
                });
                // Signals that touch a rail announcement or a photo post keep
                // the rail and grid honest too — both are single cheap GETs.
                if (this.railAnnouncements.some(p => p.id === postId)) this.loadRail();
                this.loadMedia();
            },
            onPostNew: function() {
                this.loadAll();
            },
            onPostSignal: function(d) {
                this.refetchPost(d.postId);
            },
            onSaved: function(mode) {
                if (mode === 'announcement') this.announcementComposer = false;
                else this.wallComposer = false;
                this.loadAll();
            },
            refresh: function() {
                this.loadAll();
            },
            onVisibility: function() {
                if (document.visibilityState === 'visible') this.refresh();
            }
        },
        watch: {
            ready: function() {
                this.loadAll();
            }
        },
        mounted() {
            // App.vue owns socket connect/join/disconnect; this view only
            // subscribes to change signals.
            this.loadAll();
            socket.on('connect', this.refresh);
            socket.on('post:new', this.onPostNew);
            socket.on('post:changed', this.onPostSignal);
            socket.on('vote:changed', this.onPostSignal);
            socket.on('reaction:changed', this.onPostSignal);
            socket.on('comment:changed', this.onPostSignal);
            document.addEventListener('visibilitychange', this.onVisibility);
        },
        beforeDestroy() {
            socket.off('connect', this.refresh);
            socket.off('post:new', this.onPostNew);
            socket.off('post:changed', this.onPostSignal);
            socket.off('vote:changed', this.onPostSignal);
            socket.off('reaction:changed', this.onPostSignal);
            socket.off('comment:changed', this.onPostSignal);
            document.removeEventListener('visibilitychange', this.onVisibility);
        }
    };
</script>

<style lang="scss" scoped>
    // Breathing room between the feed and the rail on desktop.
    @media screen and (min-width: 1024px) {
        .feed-column {
            padding-right: 2rem;
        }

        .rail-column {
            padding-left: 1rem;
        }
    }
</style>
